import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

interface BudgetBarProps {
  /** Current budget value */
  value: number;
  /** Original/current budget for comparison */
  originalValue: number;
  /** Callback when budget value changes */
  onChange: (value: number) => void;
  /** Callback when reset button is clicked */
  onReset: () => void;
  /** Label for the budget input */
  label?: string;
  /** Fixed width for the entire component (e.g., "w-full", "w-96") */
  width?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
}

export function BudgetBar({
  value,
  originalValue,
  onChange,
  onReset,
  label,
  width = 'w-full',
  disabled = false,
  className,
}: BudgetBarProps) {
  const hasChange = Math.abs(value - originalValue) > 0.001; // Account for floating point precision
  const budgetDifference = value - originalValue;
  const isIncrease = budgetDifference > 0;
  const isDecrease = budgetDifference < 0;

  // Local display state for the input. Keeping the visible string separate
  // from the canonical `value` lets the user pass through transient states
  // the number type can't represent — empty (mid-backspace), "1." (typing
  // a decimal in progress), leading zeros — without React's controlled-
  // input pattern snapping them back to the store value mid-typing.
  //
  // Re-sync from `value` only when the input is NOT focused. While focused,
  // an external prop change (e.g., reset button, parent state update) is
  // buffered until blur — clobbering the user's in-progress typing would
  // be worse than letting them see briefly-stale display state.
  const [displayValue, setDisplayValue] = React.useState<string>(String(value));
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(String(value));
    }
  }, [value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Always reflect what the user typed in the display, including
    // transient empty / partial-decimal states.
    setDisplayValue(raw);
    const newValue = parseFloat(raw);
    if (!isNaN(newValue) && newValue >= 0) {
      // Valid number → sync to store. Empty / NaN / negative falls through
      // without writing, so the store keeps its last valid value while the
      // user is mid-edit. handleBlur is what restores the visible field.
      onChange(newValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseFloat(displayValue);
    if (isNaN(parsed) || parsed < 0) {
      // Invalid blur (empty / malformed / negative) — restore the visible
      // field from the canonical store value. The store was never written
      // to during the invalid state, so this is a true revert.
      setDisplayValue(String(value));
      return;
    }
    // Valid blur — round to cents and sync both display + store. Sync the
    // rounded value to the store only when it actually differs from the
    // user-typed value (avoids spurious re-renders / dirty-flag flips for
    // values that are already clean cents).
    const rounded = Math.round(parsed * 100) / 100;
    setDisplayValue(String(rounded));
    if (Math.abs(rounded - parsed) > 0.0001) {
      onChange(rounded);
    }
  };

  return (
    <div className={cn(width, className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Responsive container */}
      <div className="space-y-2">
        {/* Input row with flexible layout */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={displayValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              className={cn(
                "flex-1 min-w-[80px] text-lg font-bold",
                hasChange ? "border-yellow-400 bg-yellow-50" : ""
              )}
            />
            <span className="text-lg text-gray-500 flex-shrink-0">$</span>
          </div>

          {/* Fixed-width container for reset button - always reserve space to prevent overflow */}
          <div className="w-[60px] flex-shrink-0">
            {hasChange && (
              <button
                type="button"
                onClick={onReset}
                disabled={disabled}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-200 hover:border-blue-300 rounded shadow-sm hover:shadow transition-all whitespace-nowrap font-medium"
                title="Reset to current budget"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                reset
              </button>
            )}
          </div>
        </div>

        {/* Change indicator - always reserve one line of space */}
        <div className="text-xs min-h-[20px]">
          {hasChange && (
            <>
              {isIncrease ? (
                <div className="text-green-600">
                  ↗${Math.abs(budgetDifference).toFixed(2)}: total budget will be increased.
                </div>
              ) : isDecrease ? (
                <div className="text-amber-600">
                  ↘${Math.abs(budgetDifference).toFixed(2)}: total budget will be reduced.
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
